import { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'
import { useClickAway, useIsomorphicLayoutEffect } from 'react-use'
import gsap from 'gsap'
import Flip from 'gsap/dist/Flip'
import { map } from 'lodash'
import { Search as SearchIcon } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Image from '@/components/base/lazy-image'
import Logo from '@/components/base/logo'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useAppStore } from '@/contexts/store'

import Menu from './menu'
import Toolbar from './toolbar'
import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export type HeaderProps = HTMLAttributes<HTMLDivElement> & {
  pageProps?: WPPageData
}

const Header = ({ className, pageProps, ...props }: HeaderProps) => {
  const { strings, menu } = useAppStore()
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const layout = useMemo(() => pageProps?.acf?.layout, [pageProps])
  const categories = useMemo(() => pageProps?.categories, [pageProps])
  const toolbar = useMemo(() => layout?.header.toolbar, [layout])
  const toolbarKey = useMemo(() => JSON.stringify(toolbar) || 'none', [toolbar])
  const toolbarRef = useRef<HTMLDivElement>(null!)
  const selector = useRef<HTMLDivElement>(null!)
  const menuItems = useRef<HTMLElement[]>([])
  const root = useRef<HTMLDivElement>(null!)
  const router = useRouter()

  useClickAway(root, () => setMenuOpen(false))

  useIsomorphicLayoutEffect(() => {
    gsap.killTweensOf(root.current)
    gsap
      .timeline()
      .set(root.current, { pointerEvents: 'none' }, 0)
      .set(root.current, { pointerEvents: 'auto' }, 0.3)
    setMenuOpen(false)
  }, [router])

  useEffect(() => {
    if (activeIndex < 0) return
    const state = Flip.getState(selector.current, { simple: true })
    const activeItem = menuItems.current[activeIndex]
    activeItem.append(selector.current)
    Flip.from(state, { duration: 0.5, ease: 'power2.out' })
  }, [activeIndex])

  return (
    <header
      className={cn('fixed left-0 right-0 top-0 z-header p-[1rem] md:p-[2rem]', className)}
      {...props}
    >
      <div ref={root} className={cn('relative mx-auto grid w-full max-w-[71.1rem] gap-y-[0.2rem]')}>
        <SwitchTransition mode="out-in">
          <Transition nodeRef={toolbarRef} key={toolbarKey} timeout={500} unmountOnExit>
            {(state) => (
              <Toolbar
                ref={toolbarRef}
                tools={toolbar}
                filters={categories}
                className={cn(
                  'absolute left-0 top-[calc(var(--header-height)+0.2rem)]',
                  'transition-transform duration-500 ease-out-cubic',
                  {
                    'translate-y-0': state === 'entered' || state === 'entering',
                    '-translate-y-[calc(100%+0.2rem)]':
                      state === 'exited' || state === 'exiting' || !toolbar
                  }
                )}
              />
            )}
          </Transition>
        </SwitchTransition>

        <div
          className={cn(
            'group/header',
            'relative size-full rounded-[0.7rem] px-[2rem]',
            'bg-dark text-white md:px-[3rem]'
          )}
        >
          <div className="flex h-header-height items-center justify-between">
            <Link
              href="/"
              scroll={false}
              className={cn('-m-3 rounded-[0.7rem] p-3', 'focus-ring-light')}
            >
              <span className="sr-only">{strings?.brand}</span>
              <Logo />
            </Link>

            <div className="flex h-full items-center md:space-x-[2.7rem]">
              <ul className="hidden h-full items-center justify-center space-x-[1.1rem] md:flex">
                {map(menu?.header_menu?.menu, ({ title, url, classes: theme, children }, index) => (
                  <li
                    key={`menu-item-${index}`}
                    className={cn(
                      'group/menu flex h-full items-center',
                      'transition-all delay-100 duration-500 ease-out-cubic',
                      {
                        'invisible translate-y-[1rem] opacity-0 delay-0 duration-300': menuOpen
                      }
                    )}
                  >
                    <Link
                      href={url}
                      ref={(el: any) => (menuItems.current[index] = el as any)}
                      className={cn(
                        'relative -my-[1rem] rounded-[0.7rem] px-[1.6rem] py-[1rem]',
                        'focus-ring-light'
                      )}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseLeave={() => setActiveIndex(-1)}
                      scroll={false}
                    >
                      <Typography
                        variant="medium-upper"
                        className="relative z-10 flex font-medium uppercase"
                      >
                        {title}
                      </Typography>
                    </Link>

                    {children && children.length ? (
                      <div
                        className={cn(
                          'absolute left-0 top-header-height z-0 h-auto w-full',
                          'transition-all duration-500 ease-out-cubic',
                          'invisible translate-y-[2rem] opacity-0',
                          'group-hover/menu:visible group-hover/menu:z-10 group-hover/menu:translate-y-0 group-hover/menu:opacity-100'
                        )}
                      >
                        <div
                          className={cn(
                            'grid',
                            'transition-all duration-700 ease-out-cubic',
                            'grid-rows-[0fr] group-hover/menu:grid-rows-[1fr]'
                          )}
                        >
                          <div className="mt-[0.2rem] grid overflow-hidden">
                            <div
                              className={cn(
                                'relative size-full rounded-[0.7rem] p-[2rem]',
                                'bg-dark',
                                {
                                  'border border-[#EBECEA] bg-white text-dark': theme === 'light'
                                }
                              )}
                            >
                              <ul
                                className={cn('flex justify-center space-x-[1.775rem]', {
                                  'justify-between': children.length > 3
                                })}
                              >
                                {map(children, ({ title, url, acf }, childIndex) => (
                                  <li key={`submenu-item-${childIndex}`}>
                                    <Link
                                      href={url}
                                      scroll={false}
                                      className={cn(
                                        'group/card relative grid h-auto w-[12rem] gap-y-[1.2rem] rounded-[0.3rem]',
                                        theme === 'light' ? 'focus-ring-dark' : 'focus-ring-light'
                                      )}
                                    >
                                      <span
                                        className={cn(
                                          'flex aspect-[120/140] overflow-hidden rounded-[0.3rem]',
                                          'after:absolute after:left-0 after:top-0 after:size-full after:border-[0rem] after:border-[#EBECEA]',
                                          'after:transition-all after:duration-300 after:ease-out-cubic group-hover/card:after:border-[1rem]',
                                          'transition-transform duration-500 ease-out-cubic',
                                          'scale-0 group-hover/menu:scale-100',
                                          {
                                            'after:opacity-40 after:mix-blend-overlay':
                                              theme === 'dark',
                                            'bg-light p-[2rem]': theme === 'light'
                                          }
                                        )}
                                        style={{ transitionDelay: `${0.1 + childIndex * 0.05}s` }}
                                      >
                                        <span
                                          className={cn(
                                            'relative flex size-full mix-blend-multiply',
                                            'transition-transform duration-500 ease-out-cubic',
                                            'scale-[2] group-hover/menu:scale-100'
                                          )}
                                          style={{ transitionDelay: `${0.1 + childIndex * 0.05}s` }}
                                        >
                                          {acf?.image?.sizes?.medium?.url ? (
                                            <Image
                                              src={acf?.image?.sizes?.medium?.url}
                                              alt=""
                                              fill
                                              className={cn(
                                                'object-cover',

                                                {
                                                  'object-contain mix-blend-multiply':
                                                    theme === 'light'
                                                }
                                              )}
                                            />
                                          ) : (
                                            <div className="size-full bg-gray-400" />
                                          )}
                                        </span>
                                      </span>
                                      <Typography
                                        variant="upper-small"
                                        paragraph
                                        className={cn(
                                          'font-bold uppercase',
                                          'transition-all duration-500 ease-out-cubic',
                                          'translate-y-[0.5rem] opacity-0',
                                          'group-hover/menu:translate-y-0 group-hover/menu:opacity-100'
                                        )}
                                        style={{ transitionDelay: `${0.2 + childIndex * 0.05}s` }}
                                      >
                                        {title}
                                      </Typography>
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>

              <Link
                href="/search"
                scroll={false}
                className={cn(
                  'flex shrink-0 items-center justify-center rounded-[0.5rem] bg-white text-dark md:space-x-[1.5rem]',
                  'min-h-[4.4rem] px-[1.4rem] py-[1.2rem] md:min-w-[15rem] md:px-[2.4rem]',
                  'absolute right-[6rem] top-[1.3rem] z-10 md:right-[8rem]',
                  'invisible w-auto -translate-x-[1rem] opacity-0 md:-translate-y-[1rem] md:translate-x-0',
                  'transition-all duration-500 ease-out-cubic',
                  {
                    'visible translate-x-0 opacity-100 delay-100 duration-300 md:translate-y-0':
                      menuOpen
                  },
                  'focus-ring-dark'
                )}
              >
                <Typography variant="breadcrumbs" className="hidden font-bold md:flex">
                  {strings?.labels?.search}
                </Typography>
                <SearchIcon size={16} strokeWidth="2.5" className="stroke-dark" />
              </Link>

              <div className="shrink-0">
                <button
                  ref={(el: any) =>
                    (menuItems.current[menu?.header_menu?.menu.length || 0] = el as any)
                  }
                  onMouseEnter={() => setActiveIndex(menu?.header_menu?.menu.length || 0)}
                  onMouseLeave={() => setActiveIndex(-1)}
                  onClick={() => setMenuOpen((menuOpen) => !menuOpen)}
                  className={cn(
                    'relative -mx-[1.275rem] flex size-[4.4rem] items-center justify-center rounded-[0.5rem]',
                    'focus-ring-light'
                  )}
                >
                  <span className="sr-only">{strings?.labels?.menu}</span>
                  <SVGIcon
                    symbol="plus"
                    className={cn(
                      'size-[1.2rem]',
                      'transition-transform duration-500 ease-out-cubic',
                      {
                        'rotate-[135deg]': menuOpen
                      }
                    )}
                  />
                  <div
                    ref={selector}
                    className="pointer-events-none absolute left-0 top-0 size-full"
                  >
                    <div
                      className={cn(
                        'absolute left-0 size-full rounded-[0.5rem] bg-white/10'
                        // 'transition-all duration-500 ease-out-cubic',
                        // 'scale-0 opacity-0 group-hover/header:scale-100 group-hover/header:opacity-100'
                      )}
                    />
                  </div>
                </button>
              </div>
            </div>
          </div>

          <Menu open={menuOpen} />
        </div>
      </div>
    </header>
  )
}

export default Header
